import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Box } from '@mui/material';

import { Loader } from 'ui';
import { addNotificationAction } from 'store/notifications/notificationsActions';
import { formatDate } from 'shared/functions';

import { subscriptionStatuses } from '../../shared/constants/shopSubscriptionStatuses';
import { getProductById } from '../../selectors/shopSelectors';
import { cancelSubscriptions, activateSubscriptions } from '../../services';
import { fetchShopProductsAction } from '../../store/shopActions';
import { ShopProductActivationModal } from '../ShopProduct/ShopProductActivationModal';
import { ShopProductCancellationModal } from '../ShopProduct/ShopProductCancellationModal';

const ShopSelectionListAction = ({ productId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const product = useSelector(getProductById(productId));

  const [isLoading, setIsLoading] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);

  const handleCloseCancelModal = () => setIsCancelModalOpen(false);

  const handleCloseActivateModal = () => setIsActivateModalOpen(false);

  const toggleCancelModal = () => setIsCancelModalOpen(isOpen => !isOpen);

  const toggleActivateModal = () => setIsActivateModalOpen(isOpen => !isOpen);

  const messageByStatus = {
    [subscriptionStatuses.ACTIVE_CANCELLED]:
      t('Cancelled. Subscription valid until: ') + formatDate(product.validUntil),
    [subscriptionStatuses.ENDED_CANCELLED]: t('Subscription has ended'),
    [subscriptionStatuses.ENDED_TRIAL]: t('Free trial has ended'),
    [subscriptionStatuses.ACTIVE_TRIAL]: t('Free trial active'),
  };

  const cancel = () => {
    setIsLoading(true);

    cancelSubscriptions({ products: [productId] })
      .then(() => {
        dispatch(
          addNotificationAction({
            message: t('Subscription successfully cancelled.'),
            variant: 'success',
          }),
        );
        dispatch(fetchShopProductsAction());
      })
      .catch(error => {
        if (error) {
          dispatch(
            addNotificationAction({
              message: t('Cannot cancel subscription'),
              variant: 'error',
            }),
          );
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const activate = () => {
    setIsLoading(true);

    activateSubscriptions({ products: [productId] })
      .then(() => {
        dispatch(
          addNotificationAction({
            message: t('Subscription successfully activated.'),
            variant: 'success',
          }),
        );
        dispatch(fetchShopProductsAction());
      })
      .catch(error => {
        if (error) {
          dispatch(
            addNotificationAction({
              message: t('Cannot activate subscription'),
              variant: 'error',
            }),
          );
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  if (isLoading || !product) {
    return <Loader />;
  }

  if (product.status !== subscriptionStatuses.ACTIVE) {
    return (
      <>
        <Box mb={1}>{messageByStatus[product.status]}</Box>
        {product.status === subscriptionStatuses.ACTIVE_CANCELLED && (
          <>
            <Button color="primary" variant="contained" size="small" onClick={toggleActivateModal}>
              Reactivate
            </Button>
            <ShopProductActivationModal
              product={product}
              open={isActivateModalOpen}
              onClose={handleCloseActivateModal}
              onConfirm={activate}
            />
          </>
        )}
      </>
    );
  }

  return (
    <>
      <Button color="primary" variant="outlined" size="small" onClick={toggleCancelModal}>
        Cancel
      </Button>
      <ShopProductCancellationModal
        product={product}
        open={isCancelModalOpen}
        onClose={handleCloseCancelModal}
        onConfirm={cancel}
      />
    </>
  );
};

ShopSelectionListAction.propTypes = {
  productId: PropTypes.number.isRequired,
};

export { ShopSelectionListAction };
